import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'qrcode';
import { Check, Copy, Download, DoorOpen, LoaderCircle, Music2, Plus, Radio, Search, Settings2, Smartphone, Users, X } from 'lucide-react';
import Footerbar from '../components/Footerbar';
import Navbar from '../components/Navbar';
import { clearActiveRoomId, setActiveRoomId } from '../lib/activeRoomSession';
import './ManageRoom.css';

const API = axios.create({ baseURL: 'https://apem.riderup.uk/api' });
const GENRES = ['Sertanejo', 'Funk', 'Pagode', 'MPB', 'Forro', 'Samba', 'Axe', 'Rock', 'Hip-Hop', 'Pop'];

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const getCurrentCoordinates = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
        reject(new Error('Este dispositivo não oferece acesso à localização.'));
        return;
    }

    navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
        (positionError) => reject(new Error(positionError.code === 1
            ? 'Permita o acesso à localização para criar ou entrar na sala.'
            : 'Não foi possível obter sua localização. Verifique o GPS e tente novamente.')),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
});
const videoIdOf = (item) => item?.videoId || item?.video_id || item?.id || '';
const thumbnailOf = (item) => {
    if (typeof item?.thumbnail === 'string') return item.thumbnail;
    return item?.thumbnail?.[0]?.url || item?.thumbnail?.url || item?.thumbnails?.[0]?.url || '';
};
const formatDuration = (seconds) => `${Math.floor((seconds || 0) / 60)}:${String((seconds || 0) % 60).padStart(2, '0')}`;

export default function ManageRoom() {
    const navigate = useNavigate();
    const { roomId } = useParams();
    const [searchParams] = useSearchParams();
    const token = localStorage.getItem('token');
    const joinFromQr = searchParams.get('code') || '';
    const handledQrCode = useRef('');
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(Boolean(roomId));
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState('');
    const [error, setError] = useState('');
    const [joinCode, setJoinCode] = useState(joinFromQr.toUpperCase());
    const [roomName, setRoomName] = useState('');
    const [maxDuration, setMaxDuration] = useState('5');
    const [maxSongs, setMaxSongs] = useState('10');
    const [blockedGenres, setBlockedGenres] = useState([]);
    const [bannedArtists, setBannedArtists] = useState([]);
    const [artistInput, setArtistInput] = useState('');
    const [editingRules, setEditingRules] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [inviteQrCode, setInviteQrCode] = useState('');

    const loadRoom = useCallback(async (id, quiet = false) => {
        if (!id || !token) return;
        try {
            const response = await API.get(`/rooms/${id}`, { headers: getHeaders() });
            setRoom(response.data.room);
            if (!quiet) {
                setMaxDuration(String(response.data.room.rules.max_duration_minutes));
                setMaxSongs(String(response.data.room.rules.max_songs_per_user));
                setBlockedGenres(response.data.room.rules.blocked_genres || []);
                setBannedArtists(response.data.room.rules.banned_artists || []);
                setError('');
            }
        } catch (requestError) {
            if (!quiet) {
                setError(requestError.response?.data?.message || 'Não foi possível abrir essa sala.');
                setRoom(null);
                clearActiveRoomId();
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [token]);

    const joinRoom = useCallback(async (code) => {
        if (!token || !code.trim()) return;
        setSaving(true);
        setError('');
        try {
            const coordinates = await getCurrentCoordinates();
            const response = await API.post('/rooms/join', { code: code.trim().toUpperCase(), ...coordinates }, { headers: getHeaders() });
            const id = response.data.room.id;
            setActiveRoomId(id);
            navigate(`/manage-room/${id}`, { replace: true });
        } catch (requestError) {
            setError(requestError.response?.data?.message || requestError.message || 'Não foi possível entrar na sala. Confira o código.');
        } finally {
            setSaving(false);
        }
    }, [navigate, token]);

    const disconnectFromRoom = useCallback((message) => {
        clearActiveRoomId();
        setRoom(null);
        setLoading(false);
        setError(message);
        navigate('/manage-room', { replace: true });
    }, [navigate]);

    const checkRoomLocation = useCallback(async (id) => {
        try {
            const coordinates = await getCurrentCoordinates();
            await API.post(`/rooms/${id}/heartbeat`, coordinates, { headers: getHeaders() });
        } catch (requestError) {
            if (requestError.response?.status === 403 || requestError.response?.status === 409) {
                disconnectFromRoom(requestError.response.data?.message || 'Sua localização não está dentro do limite da sala.');
            } else if (!requestError.response && requestError instanceof Error) {
                await API.post(`/rooms/${id}/leave`, {}, { headers: getHeaders() }).catch(() => {});
                disconnectFromRoom(requestError.message);
            }
        }
    }, [disconnectFromRoom]);

    useEffect(() => {
        if (!token) {
            navigate('/', { replace: true });
            return;
        }

        if (roomId) {
            loadRoom(roomId);
            return;
        }

        if (joinFromQr && handledQrCode.current !== joinFromQr) {
            handledQrCode.current = joinFromQr;
            joinRoom(joinFromQr);
            return;
        }

        const savedRoomId = localStorage.getItem('activeRoomId');
        if (savedRoomId) {
            navigate(`/manage-room/${savedRoomId}`, { replace: true });
        }
    }, [joinFromQr, joinRoom, loadRoom, navigate, roomId, token]);

    useEffect(() => {
        if (!roomId || !token) return undefined;
        const heartbeat = () => checkRoomLocation(roomId);
        heartbeat();
        const heartbeatTimer = window.setInterval(heartbeat, 15000);
        const refreshTimer = window.setInterval(() => loadRoom(roomId, true), 7000);
        return () => {
            window.clearInterval(heartbeatTimer);
            window.clearInterval(refreshTimer);
        };
    }, [checkRoomLocation, loadRoom, roomId, token]);

    const activeRoomId = room?.id;
    useEffect(() => {
        if (!activeRoomId || query.trim().length < 3) {
            setResults([]);
            setSearching(false);
            return undefined;
        }
        let cancelled = false;
        setSearching(true);
        const timer = window.setTimeout(async () => {
            try {
                const coordinates = await getCurrentCoordinates();
                const response = await API.get(`/rooms/${activeRoomId}/search`, {
                    params: { query: query.trim(), ...coordinates },
                    headers: getHeaders(),
                });
                if (!cancelled) setResults(Array.isArray(response.data) ? response.data.slice(0, 8) : []);
            } catch (requestError) {
                if (requestError.response?.status === 403) {
                    disconnectFromRoom(requestError.response.data?.message || 'Você está fora do limite de 100 metros da sala.');
                } else if (!requestError.response && requestError instanceof Error) {
                    setError(requestError.message);
                }
                if (!cancelled) setResults([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 450);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [activeRoomId, disconnectFromRoom, query]);

    const inviteUrl = useMemo(() => {
        if (!room?.join_code) return '';
        return `${window.location.origin}/manage-room?code=${room.join_code}`;
    }, [room?.join_code]);
    const downloadUrl = useMemo(() => `${window.location.origin}/downloads/aplicativo.zip`, []);

    useEffect(() => {
        if (!inviteUrl) {
            setInviteQrCode('');
            return undefined;
        }

        let active = true;
        setInviteQrCode('');
        QRCode.toDataURL(inviteUrl, { width: 320, margin: 2, errorCorrectionLevel: 'M' })
            .then((inviteCode) => { if (active) setInviteQrCode(inviteCode); })
            .catch((qrError) => console.error('Não foi possível gerar o QR Code da sala:', qrError));

        return () => { active = false; };
    }, [inviteUrl]);

    const createRoom = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        try {
            const coordinates = await getCurrentCoordinates();
            const response = await API.post('/rooms', {
                name: roomName.trim(),
                ...coordinates,
            }, { headers: getHeaders() });
            const id = response.data.room.id;
            setActiveRoomId(id);
            navigate(`/manage-room/${id}`, { replace: true });
        } catch (requestError) {
            setError(requestError.response?.data?.message || requestError.message || 'Não foi possível criar a sala.');
        } finally {
            setSaving(false);
        }
    };

    const saveRules = async (event) => {
        event.preventDefault();
        if (!room?.is_host) return;
        setSaving(true);
        setError('');
        try {
            const response = await API.patch(`/rooms/${room.id}/settings`, {
                max_duration_minutes: Number(maxDuration),
                max_songs_per_user: Number(maxSongs),
                blocked_genres: blockedGenres,
                banned_artists: bannedArtists,
            }, { headers: getHeaders() });
            setRoom(response.data.room);
            setEditingRules(false);
            setNotice('Configurações da sala salvas.');
            window.setTimeout(() => setNotice(''), 3000);
        } catch (requestError) {
            setError(requestError.response?.data?.message || 'Não foi possível salvar as regras da sala.');
        } finally {
            setSaving(false);
        }
    };

    const addArtist = (event) => {
        event.preventDefault();
        const value = artistInput.trim();
        if (value && !bannedArtists.some((artist) => artist.toLocaleLowerCase('pt-BR') === value.toLocaleLowerCase('pt-BR'))) {
            setBannedArtists((current) => [...current, value]);
            setArtistInput('');
        }
    };

    const toggleGenre = (genre) => setBlockedGenres((current) => current.includes(genre)
        ? current.filter((item) => item !== genre)
        : [...current, genre]);

    const addTrack = async (item) => {
        if (!room) return;
        setSaving(true);
        setError('');
        try {
            const coordinates = await getCurrentCoordinates();
            await API.post(`/rooms/${room.id}/requests`, { video_id: videoIdOf(item), ...coordinates }, { headers: getHeaders() });
            setNotice('Música adicionada à fila gratuita.');
            setQuery('');
            setResults([]);
            await loadRoom(room.id, true);
            window.setTimeout(() => setNotice(''), 3500);
        } catch (requestError) {
            if (requestError.response?.status === 403) {
                disconnectFromRoom(requestError.response.data?.message || 'Você está fora do limite de 100 metros da sala.');
            }
            setError(requestError.response?.data?.message || requestError.message || 'Não foi possível adicionar essa música.');
        } finally {
            setSaving(false);
        }
    };

    const copyText = async (value, label) => {
        try {
            await navigator.clipboard.writeText(value);
            setNotice(`${label} copiado.`);
            window.setTimeout(() => setNotice(''), 2500);
        } catch {
            setError('Não foi possível copiar automaticamente.');
        }
    };

    const closeRoom = async () => {
        if (!room) return;
        if (room.is_host) {
            if (!window.confirm('Encerrar esta sala? O player será desconectado e a fila será finalizada.')) return;
            setSaving(true);
            try {
                await API.post(`/rooms/${room.id}/close`, {}, { headers: getHeaders() });
            } catch (requestError) {
                setError(requestError.response?.data?.message || 'Não foi possível encerrar a sala.');
                setSaving(false);
                return;
            }
        } else {
            await API.post(`/rooms/${room.id}/leave`, {}, { headers: getHeaders() }).catch(() => {});
        }
        clearActiveRoomId();
        setRoom(null);
        setSaving(false);
        navigate('/manage-room', { replace: true });
    };

    return (
        <main className="room-page">
            <Navbar title="Salas" showLogo={false} />

            {loading ? (
                <section className="room-loading"><LoaderCircle className="room-spinner" size={28} /><span>Carregando sala...</span></section>
            ) : room ? (
                <div className="room-content">
                    <div className="room-heading">
                        <div><p className="room-kicker">SALA GRATUITA</p><h1>{room.name}</h1><p>Compartilhe o convite e escolha as músicas da fila.</p></div>
                        <button className="room-button room-button-quiet" onClick={closeRoom} disabled={saving}><DoorOpen size={17} />{room.is_host ? 'Encerrar sala' : 'Sair da sala'}</button>
                    </div>

                    <div className="room-columns">
                        <section className="room-main-column">
                            <div className="room-code-grid">
                                <div className="room-code-panel invite-panel">
                                    <div className="room-panel-title"><Users size={18} /><div><strong>Convide pessoas</strong><span>Use no PWA do cliente</span></div></div>
                                    <div className="room-code-row"><code>{room.join_code}</code><button className="room-icon-button" aria-label="Copiar código do convite" title="Copiar código" onClick={() => copyText(room.join_code, 'Código de convite')}><Copy size={17} /></button></div>
                                    <button className="room-button room-button-outline" onClick={() => copyText(inviteUrl, 'Link de convite')}><Copy size={16} />Copiar link de entrada</button>
                                    <p className="room-qr-caption">Escaneie para entrar na sala</p>
                                    {inviteQrCode && <img className="room-qr" src={inviteQrCode} alt="QR Code para entrar na sala" />}
                                </div>
                                {room.is_host && room.player_code && <div className="room-code-panel player-panel">
                                    <div className="room-panel-title"><Music2 size={18} /><div><strong>Conecte o player</strong><span>Digite este código no ApemMobile</span></div></div>
                                    <div className="room-code-row"><code>{room.player_code}</code><button className="room-icon-button" aria-label="Copiar código do player" title="Copiar código" onClick={() => copyText(room.player_code, 'Código do player')}><Copy size={17} /></button></div>
                                    <a className="room-button room-button-primary room-player-download" href={downloadUrl} download="aplicativo.zip"><Download size={18} /><span>Baixar aplicativo Android</span></a>
                                    <details className="room-install-guide">
                                        <summary><Smartphone size={16} />Como instalar e conectar</summary>
                                        <ol>
                                            <li>Baixe <strong>aplicativo.zip</strong> e abra o arquivo na pasta Downloads.</li>
                                            <li>Extraia o ZIP. Abra o arquivo <strong>aplicativo.apk</strong> que foi extraído.</li>
                                            <li>Se o Android bloquear, toque em <strong>Configurações</strong>, permita instalar apps desta fonte para o navegador ou gerenciador de arquivos e volte.</li>
                                            <li>Toque em <strong>Instalar</strong> e depois em <strong>Abrir</strong>.</li>
                                            <li>No APEM Player, informe o código de 8 caracteres acima e toque em <strong>Entrar</strong>.</li>
                                        </ol>
                                    </details>
                                    <p className="room-private-note">Este código é diferente do convite e só aparece para quem criou a sala.</p>
                                    <div className="room-player-status"><span className="room-status-dot" />Código pronto para conectar o player</div>
                                </div>}
                            </div>

                            <section className="room-section">
                                <div className="room-section-heading"><div><p className="room-kicker">MÚSICAS</p><h2>Adicionar à fila</h2></div><span className="room-free-tag">Grátis</span></div>
                                <label className="room-search"><Search size={19} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Busque uma música no YouTube" /><span>{searching ? <LoaderCircle className="room-spinner" size={18} /> : null}</span></label>
                                {query.trim().length > 0 && query.trim().length < 3 && <p className="room-helper">Digite pelo menos 3 caracteres.</p>}
                                {results.length > 0 && <div className="room-results" aria-live="polite">{results.map((item) => {
                                    const id = videoIdOf(item);
                                    return <article className="room-result" key={id || item.title}>
                                        <img src={thumbnailOf(item) || `https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" />
                                        <div className="room-result-copy"><strong>{item.title}</strong><span>{item.channelTitle || item.channel || 'YouTube'}{item.lengthText ? ` · ${item.lengthText}` : ''}</span></div>
                                        <button className="room-icon-button room-add-button" aria-label="Adicionar música à fila" title="Adicionar à fila" disabled={saving || !id} onClick={() => addTrack(item)}><Plus size={19} /></button>
                                    </article>;
                                })}</div>}
                                {query.trim().length >= 3 && !searching && results.length === 0 && <p className="room-empty-inline">Nenhuma música encontrada.</p>}
                                <p className="room-helper">Os pedidos não usam créditos. A sala valida duração, gênero e artistas bloqueados.</p>
                            </section>

                            <section className="room-section">
                                <div className="room-section-heading"><div><p className="room-kicker">PRÓXIMAS</p><h2>Fila de músicas</h2></div><span className="room-count">{room.tracks?.length || 0}</span></div>
                                {room.tracks?.length ? <div className="room-queue">{room.tracks.map((track, index) => <article className="room-queue-item" key={track.id}>
                                    <span className="room-queue-index">{String(index + 1).padStart(2, '0')}</span>
                                    <img src={track.thumbnail || `https://i.ytimg.com/vi/${track.video_id}/hqdefault.jpg`} alt="" />
                                    <div className="room-result-copy"><strong>{track.title}</strong><span>{track.requested_by || track.artist || 'Participante'} · {formatDuration(track.duration_seconds)}</span></div>
                                    {index === 0 && <span className="room-next-tag">A seguir</span>}
                                </article>)}</div> : <div className="room-empty"><Music2 size={22} /><span>A fila ainda está vazia.</span></div>}
                            </section>
                        </section>

                        <aside className="room-side-column">
                            <section className="room-section room-members-section">
                                <div className="room-section-heading"><div><p className="room-kicker">AO VIVO</p><h2>Pessoas conectadas</h2></div><span className="room-count">{room.members?.length || 0}</span></div>
                                <div className="room-members">{room.members?.map((member) => <div className="room-member" key={member.id}>
                                    <div className="room-avatar">{member.avatar ? <img src={member.avatar} alt="" /> : (member.name || '?').slice(0, 1).toUpperCase()}</div>
                                    <strong>{member.name}</strong>{member.is_host && <span>Anfitrião</span>}
                                </div>)}</div>
                            </section>
                            <section className="room-section room-rules-section">
                                <div className="room-section-heading">
                                    <div><p className="room-kicker">CONFIGURAÇÃO</p><h2>Regras da sala</h2></div>
                                    {room.is_host && <button type="button" className="room-icon-button" aria-label={editingRules ? 'Cancelar edição' : 'Editar regras'} title={editingRules ? 'Cancelar' : 'Editar regras'} onClick={() => setEditingRules((current) => !current)}><Settings2 size={18} /></button>}
                                </div>
                                {editingRules && room.is_host ? <form className="room-settings-form" onSubmit={saveRules}>
                                    <div className="room-field-grid">
                                        <label className="room-field">Máximo por música (min)<input type="number" min="1" max="30" value={maxDuration} onChange={(event) => setMaxDuration(event.target.value)} /></label>
                                        <label className="room-field">Músicas por pessoa<input type="number" min="1" max="50" value={maxSongs} onChange={(event) => setMaxSongs(event.target.value)} /></label>
                                    </div>
                                    <fieldset className="room-fieldset"><legend>Gêneros bloqueados</legend><div className="room-genre-grid">{GENRES.map((genre) => <label className="room-checkbox" key={genre}><input type="checkbox" checked={blockedGenres.includes(genre)} onChange={() => toggleGenre(genre)} /><span>{genre}</span></label>)}</div></fieldset>
                                    <fieldset className="room-fieldset"><legend>Artistas bloqueados</legend><div className="room-add-artist"><input value={artistInput} onChange={(event) => setArtistInput(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addArtist(event); } }} placeholder="Nome do artista" /><button type="button" className="room-icon-button" aria-label="Adicionar artista" title="Adicionar artista" onClick={addArtist}><Plus size={18} /></button></div>{bannedArtists.length > 0 && <div className="room-chips">{bannedArtists.map((artist) => <span key={artist}>{artist}<button type="button" aria-label={`Remover ${artist}`} onClick={() => setBannedArtists((items) => items.filter((item) => item !== artist))}><X size={13} /></button></span>)}</div>}</fieldset>
                                    <button className="room-button room-button-primary" type="submit" disabled={saving}>{saving ? <LoaderCircle className="room-spinner" size={17} /> : <Check size={17} />}Salvar regras</button>
                                </form> : <>
                                    <dl className="room-rule-list">
                                        <div><dt>Duração máxima</dt><dd>{room.rules.max_duration_minutes} min</dd></div>
                                        <div><dt>Músicas por pessoa na fila</dt><dd>{room.rules.max_songs_per_user}</dd></div>
                                    </dl>
                                    <div className="room-rule-group"><strong>Gêneros bloqueados</strong><p>{room.rules.blocked_genres?.length ? room.rules.blocked_genres.join(', ') : 'Nenhum'}</p></div>
                                    <div className="room-rule-group"><strong>Artistas bloqueados</strong><p>{room.rules.banned_artists?.length ? room.rules.banned_artists.join(', ') : 'Nenhum'}</p></div>
                                </>}
                                <div className="room-free-note"><Check size={16} />Sem cobranças nem créditos</div>
                                <div className="room-range-note"><Users size={16} /><span>Entrada e pedidos liberados até 100 m do anfitrião.</span></div>
                            </section>
                        </aside>
                    </div>
                </div>
            ) : (
                <div className="room-entry-layout">
                    <section className="room-entry-intro"><span className="room-entry-icon"><Radio size={25} /></span><p className="room-kicker">MÚSICA COM A GALERA</p><h1>Uma sala.<br />Uma playlist.</h1><p>Crie uma sala gratuita, compartilhe o código e deixe a música tocar no ApemMobile.</p><div className="room-entry-points"><span><Check size={16} />Sem cobrança</span><span><Check size={16} />Player para celular</span><span><Users size={16} />Até 100 m do anfitrião</span></div></section>
                    <div className="room-entry-forms">
                        <form className="room-section room-create-form" onSubmit={createRoom}>
                            <div className="room-section-heading"><div><p className="room-kicker">ANFITRIÃO</p><h2>Criar sala</h2></div><Plus size={19} /></div>
                            <label className="room-field">Nome da sala<input required minLength={2} maxLength={120} value={roomName} onChange={(event) => setRoomName(event.target.value)} placeholder="Ex.: Churrasco de sábado" /></label>
                            <div className="room-location-hint"><Users size={17} /><span>A localização do anfitrião será o ponto de referência. Só entra quem estiver a até 100 m.</span></div>
                            <button className="room-button room-button-primary" type="submit" disabled={saving}>{saving ? <LoaderCircle className="room-spinner" size={17} /> : <Radio size={17} />}Criar sala e abrir</button>
                        </form>

                        <form className="room-section room-join-form" onSubmit={(event) => { event.preventDefault(); joinRoom(joinCode); }}>
                            <div className="room-section-heading"><div><p className="room-kicker">CONVIDADO</p><h2>Entrar em uma sala</h2></div><Users size={19} /></div>
                            <p>Digite o código e permita a localização. Você precisa estar a até 100 m do anfitrião.</p>
                            <label className="room-field">Código de convite<input autoCapitalize="characters" maxLength={6} value={joinCode} onChange={(event) => setJoinCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="6 caracteres" /></label>
                            <button className="room-button room-button-outline" type="submit" disabled={saving || joinCode.length !== 6}>{saving ? <LoaderCircle className="room-spinner" size={17} /> : <DoorOpen size={17} />}Entrar na sala</button>
                        </form>
                    </div>
                </div>
            )}

            {(error || notice) && <div className={`room-toast ${error ? 'is-error' : ''}`} role={error ? 'alert' : 'status'}><span>{error || notice}</span><button onClick={() => { setError(''); setNotice(''); }} aria-label="Fechar mensagem"><X size={17} /></button></div>}
            <Footerbar />
        </main>
    );
}
